import * as React from 'react';
import {
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {ColumnDefinition, getItemTreatmentColors, getItemTreatmentLabelKey} from '../../../../hooks/useMonthlyChange';

interface MonthlyChangeColumnsDialogProps {
    open: boolean;
    onClose: () => void;
    editableColumns: ColumnDefinition[];
    visibleColumnIds: Set<string> | null;
    getColumnTreatmentSource: (column: ColumnDefinition) => { type?: string | null; taxBasis?: string | null };
    handleToggleColumnVisibility: (columnId: string) => void;
    handleShowAllColumns: () => void;
}

export default function MonthlyChangeColumnsDialog({
    open,
    onClose,
    editableColumns,
    visibleColumnIds,
    getColumnTreatmentSource,
    handleToggleColumnVisibility,
    handleShowAllColumns,
}: Readonly<MonthlyChangeColumnsDialogProps>): React.ReactElement {
    const {t} = useTranslation();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>{t('payroll.monthlyChange.columnsDialog.title')}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{mb: 1.5, textAlign: 'left'}}>
                    {t('payroll.monthlyChange.columnsDialog.description')}
                </Typography>
                <List disablePadding>
                    {editableColumns.map((column) => {
                        const isVisible = visibleColumnIds === null || visibleColumnIds.has(column.id);
                        const treatmentSource = getColumnTreatmentSource(column);
                        const colors = getItemTreatmentColors(treatmentSource);
                        return (
                            <ListItemButton
                                key={column.id}
                                onClick={() => handleToggleColumnVisibility(column.id)}
                                dense
                            >
                                <ListItemIcon sx={{minWidth: 36}}>
                                    <Checkbox edge="start" checked={isVisible} tabIndex={-1} disableRipple/>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body2">{column.name}</Typography>
                                            <Chip
                                                label={t(getItemTreatmentLabelKey(treatmentSource))}
                                                size="small"
                                                sx={{height: 18, fontSize: '0.65rem', backgroundColor: colors.bg, color: colors.color}}
                                            />
                                        </Stack>
                                    }
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleShowAllColumns}>{t('payroll.monthlyChange.columnsDialog.selectAll')}</Button>
                <Button variant="contained" onClick={onClose}>{t('payroll.monthlyChange.columnsDialog.apply')}</Button>
            </DialogActions>
        </Dialog>
    );
}
